'use client';

import { Select } from '@/components/atoms/Select/Select';
import { useRouter } from '@/i18n/routing';
import { useZohoClientSDK } from '@/providers/ZohoClientSDKProvider/ZohoClientSDKProvider';
import { useState } from 'react';

type WidgetSelectorEntity = string | readonly string[] | null;

interface WidgetSelectorOption {
  value: string;
  label: string;
  entity: WidgetSelectorEntity;
}

const widgetMatchesEntity = (
  widgetEntity: WidgetSelectorEntity,
  currentEntity: string,
): boolean => {
  if (widgetEntity === null) {
    return true;
  }

  if (Array.isArray(widgetEntity)) {
    return widgetEntity.includes(currentEntity);
  }

  return widgetEntity === currentEntity;
};

const WIDGETS: WidgetSelectorOption[] = [
  {
    value: 'deal-create-offer-widget',
    label: 'Deal Create Offer',
    entity: 'Deals',
  },
  {
    value: 'deal-transition-won-widget',
    label: 'Deal Transition Won',
    entity: 'Deals',
  },
  { value: 'deal-handover-widget', label: 'Deal Handover', entity: 'Deals' },
  {
    value: 'deal-handover-to-installation-widget',
    label: 'Deal Handover to Installation',
    entity: 'Deals',
  },
  {
    value: 'deal-handover-to-tech-feasibility-widget',
    label: 'Deal Handover to Tech Feasibility',
    entity: 'Deals',
  },
  {
    value: 'deal-close-and-clone-widget',
    label: 'Deal Close and Clone',
    entity: 'Deals',
  },
  {
    value: 'deal-credit-check-widget',
    label: 'Deal Credit Check',
    entity: 'Deals',
  },
  {
    value: 'deal-consolidated-activities-widget',
    label: 'Deal Consolidated Activities',
    entity: 'Deals',
  },
  {
    value: 'deal-cancellation-widget',
    label: 'Deal Cancellation',
    entity: 'Deals',
  },
  {
    value: 'deal-status-rollback-widget',
    label: 'Deal Status Rollback',
    entity: 'Deals',
  },
  { value: 'offer-switch-widget', label: 'Offer Switch', entity: 'Deals' },
  {
    value: 'lead-conversion-widget',
    label: 'Lead Conversion',
    entity: 'Leads',
  },
  {
    value: 'lead-handover-widget',
    label: 'Lead Handover',
    entity: 'Leads',
  },
  {
    value: 'lead-qualification-widget',
    label: 'Lead Qualification',
    entity: 'Leads',
  },
  {
    value: 'lead-lost-widget',
    label: 'Lead Lost',
    entity: 'Leads',
  },
  {
    value: 'heartbeat-calculator-widget',
    label: 'Heartbeat Calculator',
    entity: ['Leads', 'Deals'],
  },
  {
    value: 'address-change-widget',
    label: 'Address Change',
    entity: 'Deals',
  },
  {
    value: 'contact-create-deal-widget',
    label: 'Contact Create Deal',
    entity: 'Contacts',
  },
  {
    value: 'company-create-contact-widget',
    label: 'Company Create Contact',
    entity: 'Accounts',
  },
  {
    value: 'settings-add-campaign-id-widget',
    label: 'Settings Add Campaign ID',
    entity: null,
  },
];

export const WidgetSelector: React.FC = () => {
  const router = useRouter();
  const { currentUser, entityData } = useZohoClientSDK();
  const [selectedWidget, setSelectedWidget] = useState<string>('');
  const isAdmin = currentUser?.role?.name === 'Super Admin';

  const handleWidgetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const widgetPath = e.target.value;
    setSelectedWidget(widgetPath);
    router.push(`/${widgetPath}`);
  };

  if (!isAdmin) {
    return null;
  }

  const availableWidgets = entityData?.Entity
    ? WIDGETS.filter((widget) =>
        widgetMatchesEntity(widget.entity, entityData.Entity),
      )
    : WIDGETS;

  return (
    <div className="mb-400 flex items-center gap-200">
      <label htmlFor="widget-select" className="label-small">
        Widget:
      </label>
      <Select
        id="widget-select"
        value={selectedWidget}
        onChange={handleWidgetChange}
        className="min-w-full"
      >
        <option value="">Select a widget...</option>
        {availableWidgets.map((widget) => (
          <option key={widget.value} value={widget.value}>
            {widget.label}
          </option>
        ))}
      </Select>
    </div>
  );
};
